import { useRef, useEffect, useState } from 'react';

interface UseSilenceDetectionProps {
  onSilenceDetected?: () => void;
  silenceThreshold?: number;
  silenceDelay?: number;
  requiredSilentFrames?: number;
  debug?: boolean;
}

export const useSilenceDetection = ({
  onSilenceDetected,
  silenceThreshold = 5,
  silenceDelay = 1000,
  requiredSilentFrames = 5,
  debug = false
}: UseSilenceDetectionProps = {}) => {
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const scriptProcessorRef = useRef<ScriptProcessorNode | null>(null);
  const microphoneRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const silenceTimeoutRef = useRef<number | null>(null);
  const maxListeningTimeoutRef = useRef<number | null>(null);
  const isListeningRef = useRef(false);
  const initializationDelayRef = useRef<number | null>(null);
  const [isSilent, setIsSilent] = useState(false);
  const silenceCallbackRef = useRef<() => void>(() => {});
  const silenceTimerStartedRef = useRef(false);

  const setSilenceCallback = (callback: () => void) => {
    silenceCallbackRef.current = callback;
  };

  const setupSilenceDetection = (stream: MediaStream) => {
    try {
      cleanupAudioContext();

      audioContextRef.current = new AudioContext();
      analyserRef.current = audioContextRef.current.createAnalyser();
      microphoneRef.current = audioContextRef.current.createMediaStreamSource(stream);
      scriptProcessorRef.current = audioContextRef.current.createScriptProcessor(2048, 1, 1);

      analyserRef.current.smoothingTimeConstant = 0.3;
      analyserRef.current.fftSize = 1024;

      microphoneRef.current.connect(analyserRef.current);
      analyserRef.current.connect(scriptProcessorRef.current);
      scriptProcessorRef.current.connect(audioContextRef.current.destination);

      let consecutiveSilentFrames = 0;
      let logCounter = 0;
      let isInitialized = false;
      setIsSilent(false);

      if (debug) {
        console.log('🎤 Silence detection started');
        console.log(`📊 Threshold: ${silenceThreshold}, Required frames: ${requiredSilentFrames}, Delay: ${silenceDelay}ms`);
      }

      initializationDelayRef.current = window.setTimeout(() => {
        isInitialized = true;
        if (debug) {
          console.log('🎵 Audio context initialized, silence detection active');
        }
      }, 500);

      isListeningRef.current = true; // moved above onaudioprocess assignment

      scriptProcessorRef.current.onaudioprocess = () => {
        if (!isListeningRef.current || !isInitialized) return;

        const array = new Uint8Array(analyserRef.current!.frequencyBinCount);
        analyserRef.current!.getByteFrequencyData(array);

        const relevantFrequencies = array.slice(3, 30);
        const arraySum = relevantFrequencies.reduce((a, value) => a + value, 0);
        const average = arraySum / relevantFrequencies.length;

        if (debug && logCounter % 20 === 0) {
          console.log(`🔊 Audio level: ${average.toFixed(2)} | Silent frames: ${consecutiveSilentFrames} | Threshold: ${silenceThreshold}`);
        }
        logCounter++;

        if (average < silenceThreshold) {
          consecutiveSilentFrames++;

          if (consecutiveSilentFrames >= requiredSilentFrames && !silenceTimerStartedRef.current) {
            silenceTimerStartedRef.current = true;
            setIsSilent(true);

            if (debug) {
              console.log(`⏰ Silence detected! Starting ${silenceDelay}ms countdown...`);
            }

            if (silenceTimeoutRef.current !== null) {
              window.clearTimeout(silenceTimeoutRef.current);
            }

            silenceTimeoutRef.current = window.setTimeout(() => {
              if (debug) {
                console.log("🛑 Silence timeout reached, stopping recording");
              }
              if (isListeningRef.current) {
                if (onSilenceDetected) onSilenceDetected();
                if (silenceCallbackRef.current) silenceCallbackRef.current();
              }
            }, silenceDelay);
          }
        } else {
          if (consecutiveSilentFrames > 0 || silenceTimerStartedRef.current) {
            consecutiveSilentFrames = 0;
            silenceTimerStartedRef.current = false;
            setIsSilent(false);

            if (debug) {
              console.log(`🎵 Sound detected (${average.toFixed(2)}), canceling silence timeout`);
            }

            if (silenceTimeoutRef.current !== null) {
              window.clearTimeout(silenceTimeoutRef.current);
              silenceTimeoutRef.current = null;
            }
          }
        }
      };

      maxListeningTimeoutRef.current = window.setTimeout(() => {
        if (debug) {
          console.log("⏱️ Maximum listening time reached (30s), stopping recording");
        }
        if (isListeningRef.current) {
          if (onSilenceDetected) onSilenceDetected();
          if (silenceCallbackRef.current) silenceCallbackRef.current();
        }
      }, 30000);

    } catch (error) {
      console.error("Error setting up silence detection:", error);
    }
  };

  const cleanupAudioContext = () => {
    isListeningRef.current = false;
    setIsSilent(false);

    if (debug) {
      console.log("🧹 Cleaning up AudioContext and stopping microphone...");
    }

    if (initializationDelayRef.current !== null) {
      window.clearTimeout(initializationDelayRef.current);
      initializationDelayRef.current = null;
    }

    if (silenceTimeoutRef.current !== null) {
      window.clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }

    if (maxListeningTimeoutRef.current !== null) {
      window.clearTimeout(maxListeningTimeoutRef.current);
      maxListeningTimeoutRef.current = null;
    }

    if (scriptProcessorRef.current) {
      try {
        scriptProcessorRef.current.disconnect();
      } catch (e) {
        if (debug) console.log("Error disconnecting scriptProcessor:", e);
      }
      scriptProcessorRef.current = null;
    }

    if (analyserRef.current) {
      try {
        analyserRef.current.disconnect();
      } catch (e) {
        if (debug) console.log("Error disconnecting analyser:", e);
      }
      analyserRef.current = null;
    }

    if (microphoneRef.current) {
      try {
        microphoneRef.current.disconnect();
      } catch (e) {
        if (debug) console.log("Error disconnecting microphone:", e);
      }
      microphoneRef.current = null;
    }

    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      try {
        if (audioContextRef.current.state === 'running') {
          audioContextRef.current.suspend();
          if (debug) console.log("🎵 AudioContext suspended");
        }

        audioContextRef.current.close();
        if (debug) console.log("🔒 AudioContext closed");
      } catch (e) {
        if (debug) console.log("Error closing audioContext:", e);
      }
    }

    audioContextRef.current = null;
  };

  useEffect(() => {
    return () => {
      cleanupAudioContext();
    };
  }, []);

  return {
    setupSilenceDetection,
    cleanupAudioContext,
    isDetecting: isListeningRef.current,
    isSilent,
    setSilenceCallback
  };
};
